import {
  AfterContentChecked,
  AfterViewInit,
  Component,
  ExistingProvider,
  forwardRef,
  Input,
  OnInit,
  ViewChild,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormControl,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Utils} from '../../utils/utils';
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect';
import {IFilterValidation} from 'src/app/interfaces/employee.interface';
import {FlatPickrOutputOptions} from 'angularx-flatpickr/lib/flatpickr.directive';
import {FlatpickrDefaultsInterface} from 'angularx-flatpickr';

const DATEPICKR: ExistingProvider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => DatepickerComponent),
  multi: true,
};

/**
 * The component allows get and set date
 */
@UntilDestroy()
@Component({
  selector: 'app-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  providers: [DATEPICKR],
})
export class DatepickerComponent
  implements ControlValueAccessor, OnInit, AfterContentChecked, AfterViewInit
{
  date = new UntypedFormControl(); // control for date from ng2-flatpickr
  boxWithWeekDays!: HTMLElement[]; // box where the days of the week are displayed in the ng2-flatpickr library
  weekDays: string[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']; // correct days values of the week
  dateValue: string | null = null; // value that is sent outside
  isClose = false; // show/hide X(close) icon
  openCalendar!: HTMLElement; // element with open calendar from the ng2-flatpickr library
  rightCalendar!: HTMLElement; // element with open calendar if it displays near the right side of the page
  startOptions: FlatpickrDefaultsInterface = {
    locale: {
      firstDayOfWeek: 1,
    },
    plugins: [],
    static: false,
    dateFormat: 'd/m/Y',
  };
  disable: boolean = false;
  formatDate = Utils.formatDateToString;
  @Input() set minDeadLine(minDeadLine: Date) {
    if (!minDeadLine) return;
    this.startOptions['minDate'] = this.formatDate([minDeadLine])!;
  }
  @Input() showIcon = true;
  @Input() set deadLine(data: null | Date | '') {
    if (!data) return;

    this.startOptions['minDate'] = data;
    if (this.dateValue) {
      const transformedDateValue = Utils.formatStringToDate(this.dateValue);
      if (transformedDateValue < data) {
        this.dateValue = this.formatDate([data]);
        this.onChange(this.dateValue);
      }
    }
  }
  @Input() set disableFutureDates(value: boolean) {
    if (value) this.startOptions.maxDate = new Date();
  }

  @Input() set monthVisible(available: boolean) {
    if (available) {
      this.startOptions['plugins'] = [monthSelectPlugin({shorthand: false})];
    }
  }

  /**
   * Validation input, accept an object with the date value and set type (min or max date)
   * If the date has value set the min or max date for flatpickr option
   * If the value is empty string - remove min and max setters
   */
  @Input() set filterValidation(value: IFilterValidation) {
    if (value.date || value.date === '') {
      const datapicker = this.dataPicker.flatpickr;
      value.date
        ? value.setType === 'minDate'
          ? (this.startOptions['minDate'] = value.date)
          : (this.startOptions['maxDate'] = value.date)
        : datapicker.set(value.setType, null);
    }
  }
  /**
   * iconLeft sets icon calendar on the left/right of the date
   */
  @Input() iconLeft = false;

  /**
   * label - additional text on the left of the date
   */
  @Input() label = '';

  /**
   * fontSize sets font size of the date text inside the datepicker(max-14)
   */
  @Input() fontSize = 12;

  /**
   * disabl - disable/unable choosing date
   */
  @Input() set isDisabled(value: boolean) {
    this.disable = value;
    this.datePickerDisableHandler();
  }

  /**
   * withoutX show/hide X(close) icon
   */
  @Input() withoutX = false;
  @Input() set staticPropery(value: boolean) {
    this.startOptions.static = value;
  }
  @Output() datepickerOpened = new EventEmitter<boolean>();
  @ViewChild('datapicker') dataPicker: any;
  onChange: any = () => {
    // function must be empty
  }

  onTouched: any = () => {
    //
  }

  ngOnInit(): void {
    this.date.valueChanges.pipe(untilDestroyed(this)).subscribe(val => {
      this.dateValue = this.formatDate(val);
      if (this.onChange) this.onChange(this.dateValue);
    });
  }
  ngAfterViewInit(): void {
    const flatpickr = this.dataPicker.nativeElement._flatpickr;
    flatpickr.config = {...flatpickr.config, ...this.startOptions};
    this.boxWithWeekDays = Array.from(
      document.getElementsByClassName('flatpickr-weekdaycontainer')
    ) as HTMLElement[];
    for (const it of this.boxWithWeekDays) {
      it.innerHTML = '';
      for (const day of this.weekDays) {
        it.innerHTML += `<span class="flatpickr-weekday">${day}</span>`;
      }
    }

    this.datePickerDisableHandler();
  }
  ngAfterContentChecked(): void {
    this.openCalendar = document.getElementsByClassName(
      'flatpickr-calendar open'
    )[0] as HTMLElement;
    if (!this.openCalendar && this.isClose) this.isClose = false;
    this.rightCalendar = document.getElementsByClassName(
      'flatpickr-calendar open rightMost'
    )[0] as HTMLElement;
    if (this.rightCalendar) this.isClose = false;
  }

  datePickerDisableHandler() {
    const inputElement = this.dataPicker?.nativeElement?._flatpickr?._input;
    if (this.disable) {
      inputElement?.setAttribute('disabled', 'disabled');
    } else {
      inputElement?.removeAttribute('disabled');
    }
  }

  onClose(event: FlatPickrOutputOptions) {
    this.datepickerOpened.emit(false);
    this.onTouched();

    if (event.selectedDates.length === 0) return;
    const formatSelectedData = this.formatDate(event.selectedDates as [Date]);
    if (this.dateValue !== formatSelectedData) {
      this.dateValue = formatSelectedData;
      this.onChange(this.dateValue);
    }
  }

  onOpen() {
    this.datepickerOpened.emit(true);
  }

  writeValue(value: string): void {
    this.date.setValue(value);
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
}
